import { sha256 } from 'realm-utils/algo/hash'

interface WorkerClient {
  callByName(name: string, method: string, params: unknown[]): Promise<unknown>
}

type GetClient = () => WorkerClient

const set = async (client: WorkerClient, k: string, v: unknown, expiry = 120): Promise<unknown> => {
  const key = getRedisKey(k)
  const value_str = JSON.stringify(v)

  return client.callByName('redis-worker', 'sendCommand', [
    ['set', key, value_str, 'EX', `${expiry}`],
  ])
}

const get = async (
  client: WorkerClient,
  k: string,
): Promise<{ has: false } | { has: true; data: unknown }> => {
  const key = getRedisKey(k)
  const resp = await client.callByName('redis-worker', 'sendCommand', [['get', key]])

  if (resp === null) return { has: false }

  return { has: true, data: JSON.parse(resp as string) }
}

const getRedisKey = (k: string): string => `cache-${k}`

export interface CacheOptions {
  name?: string
  expiry?: number
  keyFn?: (...params: unknown[]) => string
}

export const cacheFn = (
  getClient: GetClient,
  fn: (...params: unknown[]) => unknown | Promise<unknown>,
  { name, expiry = 120, keyFn = defaultKeyFn }: CacheOptions = {},
): ((...params: unknown[]) => Promise<unknown>) => {
  if (!name) {
    throw new Error('missing cache name')
  }

  return async (...params: unknown[]) => {
    const client = getClient()
    const key = `${name}-${keyFn(...params)}`

    // try get cache first
    const cached = await get(client, key)

    if (cached.has) return cached.data

    const result = await fn(...params)

    // store cache
    await set(client, key, result, expiry)

    return result
  }
}

export const updateFnCache = async (
  getClient: GetClient,
  fn: (...params: unknown[]) => unknown | Promise<unknown>,
  params: unknown[] = [],
  { name, expiry = 120, keyFn = defaultKeyFn }: CacheOptions = {},
): Promise<void> => {
  if (!name) {
    throw new Error('missing cache name')
  }

  const client = getClient()
  const key = `${name}-${keyFn(...params)}`
  const result = await fn(...params)

  await set(client, key, result, expiry)
}

const defaultKeyFn = (...params: unknown[]): string => {
  const str = JSON.stringify(params, deterministicReplacer)
  // sha256 in realm-utils types returns string | undefined (undefined only for
  // non-ASCII input via the reference impl). JSON.stringify output is always
  // ASCII-safe for cache keys here, so we assert string and let runtime fail
  // loudly if that ever changes.
  return sha256(str) as string
}

const deterministicReplacer = (_: string, v: unknown): unknown =>
  typeof v !== 'object' || v === null || Array.isArray(v)
    ? v
    : Object.fromEntries(
        Object.entries(v as Record<string, unknown>).sort(([ka], [kb]) =>
          ka < kb ? -1 : ka > kb ? 1 : 0,
        ),
      )

// TODO call contract with cache
// TODO get json with cache
